import React from "react";
import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Truck, Calendar, Clock, ArrowRight, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatEntityRefWithHash } from "@/lib/shortCode";

interface TruckOption {
  truckNumber: number;
  options: string[];
}

interface ConfirmationCardData {
  type: 'order' | 'reservation';
  id: string;
  shortCode?: string | null;
  truckCount: number;
  date: string;
  firstTime: string;
  lastTime?: string;
  totalVolume?: number;
  status: string;
  truckOptions?: TruckOption[];
}


interface ReservationConfirmationCardProps {
  data: ConfirmationCardData;
  onDismiss?: () => void;
}

const cardVariants = {
  hidden: { opacity: 0, y: -20, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      type: "spring" as const, 
      stiffness: 300, 
      damping: 25 
    }
  },
  exit: { 
    opacity: 0, 
    y: -10, 
    scale: 0.95,
    transition: { duration: 0.2 }
  }
};

const checkVariants = {
  hidden: { scale: 0.5, rotate: -180 },
  visible: { 
    scale: 1, 
    rotate: 0,
    transition: { 
      type: "spring" as const, 
      stiffness: 400, 
      damping: 15,
      delay: 0.2
    }
  }
};

export const ReservationConfirmationCard: React.FC<ReservationConfirmationCardProps> = ({ 
  data, 
  onDismiss 
}) => {
  const navigate = useNavigate();
  
  const isReservation = data.type === 'reservation';
  
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    } catch {
      return dateStr;
    }
  };

  const handleViewDetails = () => {
    navigate(`/pour/${data.id}`);
    onDismiss?.();
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Card className="border-success/30 bg-success/5 border border-success/30">
        <CardContent className="p-lg">
          {/* Header with success icon */}
          <div className="flex items-start gap-md">
            <motion.div 
              variants={checkVariants}
              initial="hidden"
              animate="visible"
              className="flex-shrink-0 p-2 rounded-full bg-success/20"
            >
              <Check className="h-5 w-5 text-success" />
            </motion.div>
            
            <div className="flex-1 min-w-0">
              {/* Badge with reference */}
              <div className="flex items-center gap-sm mb-2">
                <Badge 
                  variant="outline" 
                  className="border-success/50 text-success bg-success/10"
                >
                  {isReservation ? "✓ Réservation envoyée" : "✓ Commande envoyée"}
                </Badge>
                <span className="font-mono text-body-sm text-muted-foreground">
                  {formatEntityRefWithHash(data.shortCode, data.id)}
                </span>
              </div>
              
              {/* Summary */}
              <div className="space-y-1.5 text-body-md">
                <div className="flex items-center gap-sm text-foreground">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{formatDate(data.date)}</span>
                </div>
                
                <div className="flex items-center gap-sm text-muted-foreground">
                  <Truck className="h-4 w-4" />
                  <span>{data.truckCount} camion{data.truckCount > 1 ? 's' : ''}</span>
                </div>
                
                <div className="flex items-center gap-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    {data.firstTime}
                    {data.lastTime && data.lastTime !== data.firstTime && ` → ${data.lastTime}`}
                  </span>
                </div>
                
                {data.totalVolume && data.totalVolume > 0 && (
                  <div className="flex items-center gap-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span className="font-medium text-foreground">
                      {data.totalVolume}m³ total
                    </span>
                  </div>
                )}
                
                {/* Display truck-specific options */}
                {data.truckOptions && data.truckOptions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                    {data.truckOptions.map((truck) => (
                      <div key={truck.truckNumber} className="flex items-center gap-sm text-helper text-muted-foreground">
                        <span className="font-medium">Camion {truck.truckNumber}:</span>
                        <span className="text-primary">{truck.options.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-sm mt-md">
                <Button 
                  size="sm" 
                  variant="default"
                  onClick={handleViewDetails}
                  className="gap-1.5"
                >
                  Voir {isReservation ? 'réservation' : 'commande'}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                
                {onDismiss && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={onDismiss}
                  >
                    Fermer
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export type { ConfirmationCardData };
